import { LocationFetchResponseEnvelope } from './data-contracts';
import { HttpClient, RequestParams, TraceDataType } from './http-client';

import { loggerCan } from '@/data/utils/loggerUtil';
import { logger } from '@/logging/logger';

export class Location<SecurityDataType = unknown> {
	http: HttpClient<SecurityDataType>;

	constructor(http: HttpClient<SecurityDataType>) {
		this.http = http;
	}

	/**
	 * @description Returns the state, cities, and colonies associated with a given ZIP code.
	 *
	 * @tags Location
	 * @name LocationFetchByZipcode
	 * @summary Get location information by ZIP code
	 * @request GET:/store/{storeId}/locationFetch/byZipcode
	 * @response `200` `LocationFetchResponseEnvelope` The request completed successfully.
	 * @response `400` `void` Bad request. One or more parameters are missing or invalid.
	 * @response `401` `void` Not authenticated. The user session is not valid.
	 * @response `403` `void` Forbidden. The user is not authorized to access this resource.
	 * @response `404` `void` Resource not found.
	 * @response `500` `void` Internal server error.
	 */
	locationFetchByZipcode = (
		storeId: string,
		query: {
			/**
			 * The ZIP code to look up.
			 * @example "00000"
			 */
			zipCode: string;
			/**
			 * The language identifier (for example, -5).
			 * @example "-5"
			 */
			langId: string;
			/**
			 * The format of the response. Valid values are json and xml.
			 * @default "json"
			 */
			responseFormat?: 'json' | 'xml';
		},
		params: RequestParams = {}
	) => {
		const { _requestId: requestId, skipErrorLogging, ..._params } = params as any;
		let traceData: TraceDataType | undefined;

		if (
			loggerCan('trace') &&
			(!this.http.traceDetails || this.http.traceDetails['locationFetchByZipcode'])
		) {
			traceData = {
				requestId,
				msg: 'API request',
				url: `${this.http.baseUrl}/store/${storeId}/locationFetch/byZipcode`,
				params: _params,
				query: query,
				methodName: 'locationFetchByZipcode',
			};
			logger.trace(traceData);
		}
		return this.http.request<LocationFetchResponseEnvelope, void>({
			path: `/store/${storeId}/locationFetch/byZipcode`,
			method: 'GET',
			query: query,
			format: params.format ?? 'json',
			storeId,
			traceData,
			skipErrorLogging,
			..._params,
		});
	};
}
